import type { ReactNode } from 'react'
import { CANONICAL_SECTION_KEYS } from '@emporion/config/sections'
import type { SectionRenderContext } from './section-content/types'

const CORE_SECTION_KEY_SET = new Set<string>(CANONICAL_SECTION_KEYS)
const EXTENSION_SECTION_KEY_REGEX = /^ext:[a-z0-9._-]+$/i

export type AdminExtensionRenderer = (context: SectionRenderContext) => ReactNode

export type AdminExtensionSidebarSectionRegistration = {
  id: string
  labelKey: string
  order: number
  defaultOpen?: boolean
}

export type AdminExtensionSidebarItemRegistration = {
  id: string
  key: string
  labelKey: string
  sectionId: string
  order: number
}

export type AdminExtensionRouteRegistration = {
  id: string
  key: string
  routePath: string
  renderer: AdminExtensionRenderer
  requiredCapabilities?: string[]
  isPublic?: boolean
}

export type AdminExtensionRegistration = {
  pluginId: string
  sidebarSections: AdminExtensionSidebarSectionRegistration[]
  sidebarItems: AdminExtensionSidebarItemRegistration[]
  routes: AdminExtensionRouteRegistration[]
}

export type OwnedSidebarSection = AdminExtensionSidebarSectionRegistration & {
  pluginId: string
}

export type OwnedSidebarItem = AdminExtensionSidebarItemRegistration & {
  pluginId: string
}

export type OwnedRoute = Omit<AdminExtensionRouteRegistration, 'requiredCapabilities'> & {
  pluginId: string
  requiredCapabilities: string[]
}

export type RouteResolution = {
  key: string
  routePath: string
}

export type AdminExtensionDiagnosticEvent =
  | 'extension_registered'
  | 'extension_disposed'
  | 'extension_collision_blocked'
  | 'invalid_extension_registration'
  | 'route_resolution_failed'

export type AdminExtensionLifecycleEventType =
  | 'extension:registered'
  | 'extension:disposed'
  | 'plugin:enabled'
  | 'plugin:disabled'
  | 'plugin:reloaded'

export type AdminExtensionLifecycleEvent = {
  type: AdminExtensionLifecycleEventType
  pluginId: string
  timestamp: number
  sections?: number
  items?: number
  routes?: number
}

export type AdminExtensionDiagnosticPayload = {
  event: AdminExtensionDiagnosticEvent
  message: string
  context?: Record<string, unknown>
}

export const asNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export const normalizeRoutePath = (value: string): string =>
  value.startsWith('/') ? value.replace(/\/+$/, '') || '/' : `/${value.replace(/\/+$/, '')}`

export const assertValidOrder = (order: number, label: string) => {
  if (!Number.isFinite(order)) {
    throw new Error(`Invalid ${label}: order must be a finite number`)
  }
}

export const assertValidPluginId = (pluginId: string) => {
  if (!pluginId) {
    throw new Error('Invalid extension registration: missing pluginId')
  }
}

export const assertValidSectionKey = (key: string) => {
  if (CORE_SECTION_KEY_SET.has(key)) {
    throw new Error(`Invalid extension section key "${key}": collides with core section key`)
  }
  if (!EXTENSION_SECTION_KEY_REGEX.test(key)) {
    throw new Error(`Invalid extension section key "${key}": expected "ext:<id>" format`)
  }
}

export const assertValidCapabilities = (
  route: AdminExtensionRouteRegistration,
  routeId: string,
): string[] => {
  if (route.isPublic) return []
  const required = Array.isArray(route.requiredCapabilities)
    ? route.requiredCapabilities.map((entry) => String(entry).trim()).filter(Boolean)
    : []
  if (required.length === 0) {
    throw new Error(`Invalid extension route "${routeId}": requiredCapabilities missing (set isPublic=true for public route)`)
  }
  return Array.from(new Set(required))
}

